import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Sparkles, Play, RefreshCw, X, ExternalLink, Settings, Plus, Trash2,
  CheckCircle2, AlertCircle, MinusCircle, XCircle,
  BarChart3, Users, MessageSquare, Percent,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { marketingWebAPI } from '../../api/api';
import KpiCard, { fmtNum, fmtPct, fmtFecha } from '../../components/marketing/KpiCard';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const ENGINES = [
  { key: 'gemini-search', label: 'Gemini · Search', subtitle: 'proxy AI Overviews' },
];

const ESTADO_STYLE = {
  cita:     { bg: 'bg-emerald-100 border-emerald-300 text-emerald-800', icon: CheckCircle2, label: 'Cita' },
  menciona: { bg: 'bg-amber-100 border-amber-300 text-amber-800',       icon: AlertCircle,   label: 'Menciona' },
  ausente:  { bg: 'bg-red-50 border-red-200 text-red-700',              icon: XCircle,       label: 'Ausente' },
  error:    { bg: 'bg-gray-100 border-gray-300 text-gray-600',          icon: MinusCircle,   label: 'Error' },
};

export default function MarketingWebAiVisibility() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [vista, setVista] = useState('matrix'); // 'matrix' | 'queries'
  const [runProgress, setRunProgress] = useState(null);  // { calls_processed, calls_total, queries_completadas, queries_total, pct, elapsed_s }

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingWebAPI.geoDashboard();
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error cargando GEO');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  // Si al cargar detectamos que ya hay una ronda corriendo (lanzada en otra
  // pestaña o por el cron), arrancamos polling para mostrar progreso.
  useEffect(() => {
    if (loading || !data || running) return;
    let cancelado = false;
    (async () => {
      try {
        const res = await marketingWebAPI.geoRunCurrent();
        if (cancelado) return;
        if (res.data.status === 'running') {
          setRunning(true);
          setRunProgress(res.data.progress);
          pollearProgreso();
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Polling cada 5s al endpoint /geo/runs/current. Cuando status != 'running'
  // recargamos el dashboard y paramos. Hay un cap de seguridad a los 6 minutos
  // para que no quede polleando indefinidamente si algo se atasca.
  const pollearProgreso = () => {
    const inicio = Date.now();
    const MAX_MS = 6 * 60 * 1000;  // 6 min de safety
    const tick = async () => {
      try {
        const res = await marketingWebAPI.geoRunCurrent();
        const r = res.data;
        if (r.status === 'running') {
          setRunProgress(r.progress);
          if (Date.now() - inicio > MAX_MS) {
            // Zombi: paramos polling, dejamos el TTL del backend que lo limpie
            setRunning(false);
            setRunProgress(null);
            alert('La ronda lleva más de 6 minutos. Se considera fallida. Si los resultados aparecen luego, recarga la página manualmente.');
            return;
          }
          setTimeout(tick, 5000);
          return;
        }
        if (r.status === 'completed') {
          setRunning(false);
          setRunProgress(null);
          await cargar();
          const lr = r.run;
          if (lr) {
            alert(
              `Ronda completada en ${lr.duracion_s}s:\n` +
              `· ${lr.total_queries} queries en Gemini = ${lr.total_calls} llamadas\n` +
              `· ${lr.total_hits} citas (${((lr.hit_rate || 0) * 100).toFixed(1)}%)\n` +
              `· ${lr.total_errores} errores\n` +
              `· coste ~${lr.coste_estimado_eur || 0}€`
            );
          } else {
            alert('Ronda completada. Resultados actualizados.');
          }
          return;
        }
        if (r.status === 'error') {
          setRunning(false);
          setRunProgress(null);
          alert('La ronda falló: ' + (r.error || 'error desconocido'));
          await cargar();
          return;
        }
        // status === 'idle' → terminó y no hay placeholder
        setRunning(false);
        setRunProgress(null);
        await cargar();
      } catch (e) {
        // Si falla el endpoint, reintentamos en 5s sin abortar
        setTimeout(tick, 5000);
      }
    };
    setTimeout(tick, 3000);  // primer tick a los 3s para dar margen al backend a escribir resultados
  };

  const lanzarRonda = async () => {
    if (!window.confirm('Va a lanzar una ronda GEO sobre las queries activas. Tarda ~60-90s y consume ~$0.10 en LLMs. ¿Continuar?')) return;
    setRunning(true);
    setRunProgress({ calls_processed: 0, calls_total: 0, queries_completadas: 0, queries_total: data?.queries?.length || 0, pct: 0 });
    try {
      const res = await marketingWebAPI.geoRun();
      const r = res.data;
      if (r.status === 'started' || r.status === 'already_running') {
        // Arranca polling. La barra de progreso se actualiza en cada tick.
        pollearProgreso();
        return;
      }
      // Modo legacy: respuesta síncrona (no debería pasar tras el deploy nuevo,
      // pero lo mantenemos por compatibilidad por si un backend viejo lo devuelve).
      setRunning(false);
      setRunProgress(null);
      alert(
        `Ronda completada en ${r.duracion_s}s:\n` +
        `· ${r.total_queries} queries en Gemini = ${r.total_calls} llamadas\n` +
        `· ${r.total_hits} citas (${(r.hit_rate * 100).toFixed(1)}%)\n` +
        `· ${r.total_errores} errores\n` +
        `· coste ~${r.coste_estimado_eur}€`
      );
      await cargar();
    } catch (e) {
      setRunning(false);
      setRunProgress(null);
      alert('Error lanzando ronda: ' + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div className="p-6 text-center text-gray-500">Cargando GEO…</div>;
  if (error) return <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">{error}</div>;
  if (!data) return null;

  const lr = data.last_run;
  const haRunOK = !!lr && data.matrix.length > 0;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 bg-white border rounded-lg p-3">
        <Sparkles className="w-5 h-5 text-duetto-primary" />
        <span className="font-semibold text-gray-900">AI Visibility (GEO)</span>
        <div className="flex-1" />
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setVista('matrix')}
            className={`px-3 py-1 text-sm rounded-md ${vista === 'matrix' ? 'bg-white text-duetto-primary shadow-sm font-semibold' : 'text-gray-600'}`}
          >Matriz</button>
          <button
            onClick={() => setVista('queries')}
            className={`px-3 py-1 text-sm rounded-md ${vista === 'queries' ? 'bg-white text-duetto-primary shadow-sm font-semibold' : 'text-gray-600'}`}
          >Queries</button>
        </div>
        <button
          onClick={lanzarRonda}
          disabled={running || data.queries.length === 0}
          className="flex items-center gap-2 px-3 py-1.5 bg-duetto-primary text-white text-sm rounded-md hover:bg-opacity-90 disabled:opacity-50"
          title="Lanza una ronda manual (~60-90s)"
        >
          {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? 'Ejecutando…' : 'Lanzar ronda'}
        </button>
      </div>

      {/* Barra de progreso de la ronda en curso */}
      {running && runProgress && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-blue-900">
              Procesando ronda GEO…
            </span>
            <span className="text-blue-700 tabular-nums">
              {runProgress.queries_completadas}/{runProgress.queries_total} queries
              {runProgress.calls_total > 0 && (
                <> · {runProgress.calls_processed}/{runProgress.calls_total} llamadas</>
              )}
              {runProgress.elapsed_s != null && (
                <> · {runProgress.elapsed_s}s</>
              )}
            </span>
          </div>
          <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-duetto-primary transition-all duration-500"
              style={{ width: `${Math.min(100, runProgress.pct || 0)}%` }}
            />
          </div>
          <p className="text-xs text-blue-700">
            Puedes seguir navegando. La pestaña se recarga sola al terminar
            (cap de seguridad 6 min). Si cierras la ventana, la ronda sigue
            corriendo en el backend.
          </p>
        </div>
      )}

      {/* KPI cabecera */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Queries activas" value={fmtNum(data.queries.filter(q => q.activa).length)} icon={Sparkles} hint={`${data.queries.length} en catálogo`} />
        <KpiCard label="Hit rate último run" value={lr ? fmtPct(lr.hit_rate) : '—'} icon={CheckCircle2} hint={lr ? `${lr.total_hits}/${lr.total_calls} calls` : 'sin runs aún'} />
        <KpiCard label="Último run" value={lr ? fmtFecha(lr.ejecutado_en) : '—'} hint={lr ? `${lr.trigger} · ${lr.duracion_s}s` : ''} />
        <KpiCard label="Coste estimado" value={lr?.coste_estimado_eur != null ? `${lr.coste_estimado_eur.toFixed(2)} €` : '—'} hint="LLMs · por ronda" />
      </div>

      {vista === 'matrix' && (
        <>
          {!haRunOK && <EmptyRunHint />}
          {haRunOK && <GeoMatrix matrix={data.matrix} />}
          {data.serie?.length > 0 && <HitRateSerie serie={data.serie} />}
          <TraficoIaSection />
        </>
      )}

      {vista === 'queries' && <GeoQueriesCRUD queries={data.queries} onChange={cargar} />}
    </div>
  );
}

function EmptyRunHint() {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
      <Sparkles className="w-8 h-8 text-amber-600 mx-auto mb-2" />
      <h3 className="font-semibold text-gray-900 mb-1">Aún no hay ronda GEO ejecutada</h3>
      <p className="text-sm text-gray-600 max-w-md mx-auto">
        Pulsa <strong>Lanzar ronda</strong> arriba para preguntar a Gemini (con búsqueda web) por
        cada query del catálogo y ver si nos citan. Tarda ~60-90 segundos.
      </p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Matriz queries × engines
// ────────────────────────────────────────────────────────────────────

function GeoMatrix({ matrix }) {
  const [modalResult, setModalResult] = useState(null);

  // Loader de resultado completo para el modal
  const verCelda = async (resultId) => {
    try {
      const res = await marketingWebAPI.geoResultDetail(resultId);
      setModalResult(res.data);
    } catch (e) {
      alert('Error cargando respuesta: ' + e.message);
    }
  };

  return (
    <>
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b bg-gray-50">
              <tr>
                <th className="text-left py-2 px-3 font-medium">Query objetivo</th>
                {ENGINES.map((e) => (
                  <th key={e.key} className="py-2 px-3 font-medium text-center">
                    <div>{e.label}</div>
                    <div className="text-[10px] font-normal text-gray-400">{e.subtitle}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.map((row) => (
                <tr key={row.query.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-2 px-3">
                    <div className="text-gray-900 font-medium">{row.query.texto}</div>
                    {row.query.cluster && (
                      <div className="text-[10px] text-gray-500 mt-0.5">{row.query.cluster}</div>
                    )}
                  </td>
                  {ENGINES.map((e) => {
                    const cell = row.engines[e.key];
                    if (!cell) {
                      return (
                        <td key={e.key} className="py-2 px-3 text-center text-[11px] text-gray-400">—</td>
                      );
                    }
                    const style = ESTADO_STYLE[cell.estado];
                    const Icon = style.icon;
                    return (
                      <td key={e.key} className="py-2 px-3">
                        <button
                          onClick={() => verCelda(cell.result_id)}
                          className={`w-full inline-flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded border ${style.bg} hover:opacity-80 transition`}
                          title="Ver respuesta completa"
                        >
                          <span className="flex items-center gap-1 text-xs font-semibold">
                            <Icon className="w-3.5 h-3.5" />
                            {style.label}
                          </span>
                          {cell.mentions_blog_urls?.length > 0 && (
                            <span className="text-[9px] opacity-70">
                              {cell.mentions_blog_urls.length} blog url{cell.mentions_blog_urls.length > 1 ? 's' : ''}
                            </span>
                          )}
                          {cell.competidores_mencionados?.length > 0 && (
                            <span className="text-[9px] text-gray-600">
                              vs {cell.competidores_mencionados.length} comp.
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalResult && (
        <ResultModal result={modalResult} onClose={() => setModalResult(null)} />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Modal con la respuesta completa de una celda
// ────────────────────────────────────────────────────────────────────

function ResultModal({ result, onClose }) {
  const engine = ENGINES.find((e) => e.key === result.engine) || { label: result.engine };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <div className="text-xs text-gray-500">{engine.label} · {result.model_real}</div>
            <h3 className="font-semibold text-gray-900 mt-0.5">{result.query_texto}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto p-4 space-y-4">
          {result.error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              <strong>Error:</strong> {result.error}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge color={result.mentions_duetto ? 'green' : 'red'}>
                  {result.mentions_duetto ? 'Menciona Duetto' : 'No menciona Duetto'}
                </Badge>
                <Badge color={result.mentions_domain ? 'green' : 'gray'}>
                  {result.mentions_domain ? 'Menciona dominio' : 'Sin dominio'}
                </Badge>
                <Badge color={(result.mentions_blog_urls || []).length > 0 ? 'green' : 'gray'}>
                  {(result.mentions_blog_urls || []).length} URLs blog
                </Badge>
                {(result.competidores_mencionados || []).map((c) => (
                  <Badge key={c} color="amber">vs {c}</Badge>
                ))}
                <Badge color="gray">{result.duracion_ms} ms</Badge>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Respuesta del LLM</div>
                <div className="bg-gray-50 border rounded-lg p-3 text-sm text-gray-800 whitespace-pre-wrap">
                  {result.respuesta || '(respuesta vacía)'}
                </div>
              </div>
              {result.citations?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Grounding citations ({result.citations.length})</div>
                  <ul className="text-xs space-y-1">
                    {result.citations.map((c, i) => (
                      <li key={i}>
                        <a href={c} target="_blank" rel="noopener noreferrer" className="text-duetto-primary hover:underline inline-flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          <span className="truncate">{c}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.mentions_blog_urls?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">URLs del blog detectadas</div>
                  <ul className="text-xs space-y-1">
                    {result.mentions_blog_urls.map((u) => (
                      <li key={u} className="text-emerald-700">{u}</li>
                    ))}
                  </ul>
                </div>
              )}
              <details>
                <summary className="text-xs text-gray-500 cursor-pointer">Ver prompt enviado</summary>
                <pre className="mt-2 bg-gray-50 border rounded p-2 text-[11px] text-gray-700 whitespace-pre-wrap">{result.prompt}</pre>
              </details>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Badge({ children, color = 'gray' }) {
  const cls = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
  }[color];
  return <span className={`px-2 py-0.5 rounded border text-[11px] ${cls}`}>{children}</span>;
}

// ────────────────────────────────────────────────────────────────────
// Serie temporal de hit_rate
// ────────────────────────────────────────────────────────────────────

function HitRateSerie({ serie }) {
  const chart = {
    labels: serie.map((r) => fmtFecha(r.ejecutado_en)),
    datasets: [
      {
        label: 'Hit rate',
        data: serie.map((r) => (r.hit_rate || 0) * 100),
        borderColor: '#1A9169',
        backgroundColor: 'rgba(26,145,105,0.10)',
        fill: true,
        tension: 0.3,
      },
    ],
  };
  const chartOpts = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: {
      y: {
        beginAtZero: true, suggestedMax: 100,
        ticks: { callback: (v) => v + '%' },
        title: { display: true, text: '% queries que nos citan' },
      },
    },
  };
  return (
    <div className="bg-white border rounded-lg p-4">
      <h3 className="font-semibold text-gray-900 mb-3">Evolución del hit rate</h3>
      <Line data={chart} options={chartOpts} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Tráfico desde asistentes de IA (GA4)
//
// La matriz de arriba mide si nos citan; esto mide si esas citas traen
// visitas. Carga independiente del dashboard GEO a propósito: si GA4 falla
// (credenciales, cuota) la matriz sigue viéndose igual.
// ────────────────────────────────────────────────────────────────────

const RANGOS_IA = [30, 90, 365];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const fmtMes = (mes) => {
  const [anio, m] = (mes || '').split('-');
  const i = parseInt(m, 10) - 1;
  return MESES_CORTOS[i] ? `${MESES_CORTOS[i]} ${anio}` : mes;
};

function TraficoIaSection() {
  const [dias, setDias] = useState(90);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);

  const cargarTrafico = useCallback(async (forzar = false) => {
    setCargando(true);
    setErrorCarga(null);
    try {
      const res = await marketingWebAPI.geoTraficoIa(dias, forzar);
      setData(res.data);
    } catch (e) {
      setErrorCarga(e?.response?.data?.detail || 'Error cargando el tráfico de IA');
      setData(null);
    } finally {
      setCargando(false);
    }
  }, [dias]);

  useEffect(() => { cargarTrafico(false); }, [cargarTrafico]);

  const chartMes = useMemo(() => {
    const meses = data?.por_mes || [];
    return {
      labels: meses.map((m) => fmtMes(m.mes)),
      datasets: [
        {
          label: 'Sesiones',
          data: meses.map((m) => m.sesiones || 0),
          backgroundColor: 'rgba(26,145,105,0.75)',
          borderColor: '#1A9169',
          borderWidth: 1,
        },
        {
          label: 'Contactos',
          data: meses.map((m) => m.conversiones || 0),
          backgroundColor: 'rgba(103,206,169,0.75)',
          borderColor: '#67CEA9',
          borderWidth: 1,
        },
      ],
    };
  }, [data]);

  const chartMesOpts = {
    responsive: true,
    plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  const totales = data?.totales;
  const disponible = data?.disponible !== false;

  return (
    <div className="bg-white rounded-xl border p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h3 className="font-semibold text-gray-900">Tráfico desde asistentes de IA</h3>
          <p className="text-xs text-gray-500 mt-1">
            Visitas a la web que llegan desde ChatGPT, Gemini, Perplexity y similares, según GA4.
            Los clics desde Google AI Overviews no son separables y no están incluidos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {RANGOS_IA.map((d) => (
              <button
                key={d}
                onClick={() => setDias(d)}
                className={`px-2.5 py-1 text-xs rounded-md ${dias === d ? 'bg-white text-duetto-primary shadow-sm font-semibold' : 'text-gray-600'}`}
              >{d} días</button>
            ))}
          </div>
          <button
            onClick={() => cargarTrafico(true)}
            disabled={cargando}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border hover:bg-gray-50 disabled:opacity-50"
            title="Vuelve a consultar GA4 ignorando la cache de 6 horas"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${cargando ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      {cargando && <p className="text-sm text-gray-500">Cargando datos de GA4...</p>}

      {!cargando && errorCarga && (
        <div className="bg-gray-50 border rounded-lg p-4 text-sm text-gray-600">{errorCarga}</div>
      )}

      {!cargando && !errorCarga && !disponible && (
        <div className="bg-gray-50 border rounded-lg p-4 text-sm text-gray-600">
          {data?.error || 'GA4 no está disponible ahora mismo.'}
        </div>
      )}

      {!cargando && !errorCarga && disponible && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Sesiones" value={fmtNum(totales?.sesiones)} icon={BarChart3} hint={`${data.rango?.desde} a ${data.rango?.hasta}`} />
            <KpiCard label="Usuarios" value={fmtNum(totales?.usuarios)} icon={Users} />
            <KpiCard label="Contactos" value={fmtNum(totales?.conversiones)} icon={MessageSquare} hint="eventos clave" />
            <KpiCard
              label="% del tráfico web"
              value={`${(totales?.pct_trafico ?? 0).toFixed(1)}%`}
              icon={Percent}
              hint={`${fmtNum(data.total_sesiones_web)} sesiones totales`}
            />
          </div>

          {data.por_mes?.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Evolución por mes</div>
              <Bar data={chartMes} options={chartMesOpts} />
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Por asistente</div>
              {data.fuentes?.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-500 bg-gray-50 border-b">
                      <tr>
                        <th className="text-left py-1.5 px-3 font-medium">Asistente</th>
                        <th className="text-right py-1.5 px-3 font-medium">Sesiones</th>
                        <th className="text-right py-1.5 px-3 font-medium">Usuarios</th>
                        <th className="text-right py-1.5 px-3 font-medium">Contactos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.fuentes.map((f) => (
                        <tr key={f.fuente} className="border-b last:border-0">
                          <td className="py-1.5 px-3 text-gray-900">{f.fuente}</td>
                          <td className="py-1.5 px-3 text-right tabular-nums">{fmtNum(f.sesiones)}</td>
                          <td className="py-1.5 px-3 text-right tabular-nums text-gray-600">{fmtNum(f.usuarios)}</td>
                          <td className="py-1.5 px-3 text-right tabular-nums text-gray-600">{fmtNum(f.conversiones)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Sin visitas desde asistentes de IA en este rango.</p>
              )}
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Páginas de entrada</div>
              {data.landing_pages?.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-500 bg-gray-50 border-b">
                      <tr>
                        <th className="text-left py-1.5 px-3 font-medium">Página</th>
                        <th className="text-right py-1.5 px-3 font-medium">Sesiones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.landing_pages.map((p) => (
                        <tr key={p.path} className="border-b last:border-0">
                          <td className="py-1.5 px-3 text-gray-700 truncate max-w-[280px]" title={p.path}>{p.path}</td>
                          <td className="py-1.5 px-3 text-right tabular-nums">{fmtNum(p.sesiones)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Sin páginas de entrada registradas.</p>
              )}
            </div>
          </div>

          {data.conversiones_por_evento?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-gray-500">Contactos por evento:</span>
              {data.conversiones_por_evento.map((c) => (
                <Badge key={c.evento} color="green">{c.evento}: {fmtNum(c.total)}</Badge>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// CRUD de queries
// ────────────────────────────────────────────────────────────────────

function GeoQueriesCRUD({ queries, onChange }) {
  const [editing, setEditing] = useState(null); // {texto, cluster, intencion, activa, ...}

  const guardar = async (q) => {
    try {
      if (q.id) {
        await marketingWebAPI.geoUpdateQuery(q.id, q);
      } else {
        await marketingWebAPI.geoCreateQuery(q);
      }
      setEditing(null);
      onChange();
    } catch (e) {
      alert('Error guardando: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const borrar = async (id) => {
    if (!window.confirm('¿Borrar esta query del catálogo?')) return;
    try {
      await marketingWebAPI.geoDeleteQuery(id);
      onChange();
    } catch (e) {
      alert('Error borrando: ' + e.message);
    }
  };

  const toggleActiva = async (q) => {
    await guardar({ ...q, activa: !q.activa });
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-600">{queries.length} queries en catálogo</span>
        <button
          onClick={() => setEditing({ texto: '', cluster: '', intencion: 'transaccional', target_url: '', activa: true, notas: '' })}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-duetto-primary text-white text-sm rounded-md hover:bg-opacity-90"
        >
          <Plus className="w-4 h-4" /> Nueva query
        </button>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 border-b bg-gray-50">
            <tr>
              <th className="text-left py-2 px-3 font-medium">Texto</th>
              <th className="text-left py-2 px-3 font-medium">Cluster</th>
              <th className="text-left py-2 px-3 font-medium">Intención</th>
              <th className="text-left py-2 px-3 font-medium">Target</th>
              <th className="py-2 px-3 font-medium text-center">Activa</th>
              <th className="py-2 px-3 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {queries.map((q) => (
              <tr key={q.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-2 px-3 text-gray-900">{q.texto}</td>
                <td className="py-2 px-3 text-xs text-gray-600">{q.cluster || '—'}</td>
                <td className="py-2 px-3 text-xs text-gray-600">{q.intencion}</td>
                <td className="py-2 px-3 text-xs text-gray-500 truncate max-w-[200px]">{q.target_url || '—'}</td>
                <td className="py-2 px-3 text-center">
                  <input type="checkbox" checked={q.activa} onChange={() => toggleActiva(q)} />
                </td>
                <td className="py-2 px-3 text-right">
                  <button onClick={() => setEditing({ ...q })} className="text-duetto-primary hover:underline text-xs mr-3">Editar</button>
                  <button onClick={() => borrar(q.id)} className="text-red-600 hover:underline text-xs inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Borrar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <QueryFormModal query={editing} onSave={guardar} onClose={() => setEditing(null)} />}
    </div>
  );
}

function QueryFormModal({ query, onSave, onClose }) {
  const [form, setForm] = useState(query);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="font-semibold text-gray-900">{form.id ? 'Editar query' : 'Nueva query GEO'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <Field label="Texto de la query *">
            <input
              value={form.texto || ''}
              onChange={(e) => setForm({ ...form, texto: e.target.value })}
              placeholder="catering corporativo madrid"
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </Field>
          <Field label="Cluster (opcional)">
            <input
              value={form.cluster || ''}
              onChange={(e) => setForm({ ...form, cluster: e.target.value })}
              placeholder="catering-corporativo"
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </Field>
          <Field label="Intención">
            <select
              value={form.intencion || 'transaccional'}
              onChange={(e) => setForm({ ...form, intencion: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm"
            >
              <option value="transaccional">Transaccional</option>
              <option value="informacional">Informacional</option>
              <option value="comparativa">Comparativa</option>
            </select>
          </Field>
          <Field label="URL objetivo (opcional)">
            <input
              value={form.target_url || ''}
              onChange={(e) => setForm({ ...form, target_url: e.target.value })}
              placeholder="/empresas/desayunos/"
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </Field>
          <Field label="Notas">
            <textarea
              value={form.notas || ''}
              onChange={(e) => setForm({ ...form, notas: e.target.value })}
              rows={2}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.activa} onChange={(e) => setForm({ ...form, activa: e.target.checked })} />
            Activa (incluir en próximas rondas)
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t p-3">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border hover:bg-gray-50">Cancelar</button>
          <button
            onClick={() => onSave(form)}
            disabled={!form.texto?.trim()}
            className="px-3 py-1.5 text-sm rounded-md bg-duetto-primary text-white hover:bg-opacity-90 disabled:opacity-50"
          >Guardar</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}
